import { useCallback, useRef, useState } from "react";
import { notificationService } from "@/services/api";
import { useUnreadNotificationsCount } from "@/hooks/useUnreadNotificationsCount";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { relativeTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LoadMoreSentinel } from "./LoadMoreSentinel";
import { SkeletonList } from "./Skeletons";
import { EmptyState } from "./EmptyState";
import {
  Hexagon, Wrench, BatteryLow, Gauge, Cpu, Bell, CheckCheck, Loader2,
} from "lucide-react";

const ICONS = {
  geofence: Hexagon,
  health: Wrench,
  battery: BatteryLow,
  driving: Gauge,
  device: Cpu,
};

const SEV = {
  critical: "text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/25",
  warning: "text-amber-600 dark:text-amber-400 bg-amber-400/10 border-amber-400/20",
  info: "text-sky-600 dark:text-sky-400 bg-sky-400/10 border-sky-400/20",
};

function NotifRow({ n, onRead }) {
  const Icon = ICONS[n.icon] || Bell;
  return (
    <button
      onClick={() => !n.read && onRead(n.id)}
      className={cn(
        "flex w-full gap-3 rounded-xl border p-3 text-left transition hover:border-border hover:bg-card/70",
        n.read ? "border-border/40 bg-card/20 opacity-70" : "border-border/50 bg-card/40"
      )}
      data-testid="notification-item"
    >
      <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border", SEV[n.severity])}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="flex items-center gap-1.5 text-sm font-medium leading-tight">
            {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
            {n.title}
          </p>
          <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(n.time)}</span>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {n.vehicle && <span className="text-foreground/80">{n.vehicle} · </span>}{n.description}
        </p>
      </div>
    </button>
  );
}

export function NotificationDrawer() {
  const [open, setOpen] = useState(false);
  const unreadCount = useUnreadNotificationsCount();
  const scrollRef = useRef(null);

  // `before` walks back through older notifications a page at a time —
  // the panel is sized to fit only a handful at once (see the max-h below),
  // so most of the history stays unfetched until someone actually scrolls.
  const fetchPage = useCallback(async (cursor) => {
    const { notifications, hasMore } = await notificationService.list({ before: cursor, limit: 20 });
    // `.time` is a Date (see mapNotification) — the backend's `before` param
    // needs an ISO string, not whatever axios would coerce a bare Date into.
    const nextCursor = notifications.length
      ? notifications[notifications.length - 1].time.toISOString()
      : cursor;
    return { items: notifications, nextCursor, hasMore };
  }, []);

  const { items: data, setItems, loading, loadingMore, hasMore, loadMore } = useInfiniteScroll(fetchPage, [open]);

  const today = data.filter((n) => n.group === "today");
  const earlier = data.filter((n) => n.group === "earlier");
  const hasUnread = data.some((n) => !n.read);

  const markRead = async (id) => {
    try {
      await notificationService.markRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      /* non-critical — leave it unread rather than surface an error toast */
    }
  };

  const markAllRead = async () => {
    try {
      await notificationService.markAllRead();
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* non-critical */
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-trigger"
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-secondary/60 hover:text-foreground"
        >
          <Bell className="h-[18px] w-[18px]" />
          {unreadCount > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="notification-drawer">
        {/* `pt-4` instead of the header hugging the panel's top edge —
            shifted down a bit so "Mark all read" doesn't sit flush against
            the popover's rounded corner. */}
        <div className="flex items-center justify-between px-4 pb-3 pt-4">
          <h3 className="font-display text-base font-semibold">Notifications</h3>
          <button
            onClick={markAllRead}
            disabled={!hasUnread}
            className="flex items-center gap-1.5 text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-40"
            data-testid="mark-all-read"
          >
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        </div>

        {/* No fixed height: the panel only opens as tall as its content up
            to this cap (roughly 5-6 rows), then scrolls — it doesn't claim
            a full-length drawer's worth of space for 2 notifications. */}
        <div ref={scrollRef} className="max-h-[420px] space-y-4 overflow-y-auto px-4 pb-4">
          {loading ? (
            <SkeletonList rows={4} />
          ) : data.length === 0 ? (
            <EmptyState icon={Bell} title="You're all caught up" description="No new notifications right now." />
          ) : (
            <>
              {today.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Today</h4>
                  {today.map((n) => <NotifRow key={n.id} n={n} onRead={markRead} />)}
                </div>
              )}
              {earlier.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Earlier</h4>
                  {earlier.map((n) => <NotifRow key={n.id} n={n} onRead={markRead} />)}
                </div>
              )}
              {hasMore && (
                <>
                  <LoadMoreSentinel onVisible={loadMore} disabled={loadingMore} root={scrollRef.current} testId="notifications-load-more" />
                  {loadingMore && (
                    <div className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading older notifications…
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
